import React, { useState, useEffect } from 'react';
import { User } from '../../types/user';
import { Organization, OrganizationUser } from '../../types/organization';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  UserIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '../common/Tooltip';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
interface OrganizationUsersProps {
  organization: Organization;
  onOrganizationUpdate: (org: Organization) => void;
}

const OrganizationUsers: React.FC<OrganizationUsersProps> = ({
  organization,
  onOrganizationUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [userToDelete, setUserToDelete] = useState<OrganizationUser | null>(null);
  const { t } = useLanguage();


  // Biztosítjuk, hogy mindig legyen egy üres tömb, ha nincs users
  const users = organization?.users || [];

  // Új felhasználó hozzáadása
  const handleAddUser = async () => {
    try {
      setLoading(true);
      // Felhasználó keresése email alapján
      const q = query(
        collection(db, 'users'),
        where('email', '==', newUserEmail)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        toast.error(t('common.messages.organization.emailNotFound'));
        return;
      }

      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();

      // Ellenőrizzük, hogy nincs-e már a szervezetben
      if (organization.users.some(u => u.userId === userDoc.id)) {
        toast.error(t('common.messages.organization.userAlreadyExists'));
        return;
      }

      const newOrgUser: OrganizationUser = {
        userId: userDoc.id,
        email: userData.email,
        role: 'user',
        addedAt: new Date()
      };

      // Frissítjük a szervezet dokumentumát
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        users: arrayUnion(newOrgUser),
        updatedAt: serverTimestamp()
      });

      // Frissítjük a felhasználó dokumentumát az organizationId-val
      const userRef = doc(db, 'users', userDoc.id);
      await updateDoc(userRef, {
        organizationId: organization.id,
        updatedAt: serverTimestamp()
      });

      // Frissítjük a lokális state-et
      const updatedOrg = {
        ...organization,
        users: [...organization.users, newOrgUser]
      };
      onOrganizationUpdate(updatedOrg);
      setNewUserEmail('');
      toast.success(t('common.messages.organization.userAdded'));
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(t('common.messages.organization.userAddError'));
    } finally {
      setLoading(false);
    }
  };

  // Felhasználó eltávolítása
  const handleRemoveUser = (user: OrganizationUser) => {
    setUserToDelete(user);
  };

  const confirmRemoveUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      // Frissítjük a szervezet dokumentumát
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        users: arrayRemove(userToDelete),
        updatedAt: serverTimestamp()
      });

      // Frissítjük a felhasználó dokumentumát, töröljük az organizationId-t
      const userRef = doc(db, 'users', userToDelete.userId);
      await updateDoc(userRef, {
        organizationId: '',
        updatedAt: serverTimestamp()
      });

      // Frissítjük a lokális state-et
      const updatedOrg = {
        ...organization,
        users: organization.users.filter(u => u.userId !== userToDelete.userId)
      };
      onOrganizationUpdate(updatedOrg);
      toast.success(t('common.messages.organization.userRemoved'));
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error(t('common.messages.organization.userRemoveError'));
    } finally {
      setUserToDelete(null);
      setLoading(false);
    }
  };

  const roleChip = (role: string) => role === 'admin'
    ? <span className="inline-flex items-center px-2 h-5 rounded-full text-[11px] font-medium bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{t('organizations.roles.administrator')}</span>
    : <span className="inline-flex items-center px-2 h-5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200">{t('organizations.roles.user')}</span>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="text-xs text-gray-500 dark:text-gray-400 sm:mr-auto">{users.length} tag · a tulajdonos automatikusan tag</div>
        <div className="flex gap-2">
          <input
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUser(); } }}
            placeholder="E-mail cím – meglévő YouCRM felhasználó"
            disabled={loading}
            className="w-full sm:w-72 h-9 border rounded-lg px-3 text-sm"
          />
          <button type="button" onClick={handleAddUser} disabled={loading || !newUserEmail.trim()} className="crm-btn crm-btn-primary h-9 whitespace-nowrap">
            <PlusIcon className="w-4 h-4 mr-1" />{t('organizations.fields.users.add')}
          </button>
        </div>
      </div>

      <div className="crm-card overflow-hidden">
        {users.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Még nincs meghívott tag. Add meg egy regisztrált felhasználó e-mail címét.</div>
        ) : (
          <ul className="divide-y divide-[var(--crm-border)]">
            {users.map(user => (
              <li key={user.userId} className="flex items-center gap-3 px-3 py-2.5">
                <span className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-[11px] font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center flex-shrink-0">
                  {(user.email || '?').slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{user.email}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    {roleChip(user.role)}
                    {user.addedAt && <span>hozzáadva: {(() => { const d: any = user.addedAt; const dt = d?.toDate ? d.toDate() : new Date(d); return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('hu-HU'); })()}</span>}
                  </div>
                </div>
                <Tooltip content="Eltávolítás a szervezetből">
                  <button type="button" onClick={() => handleRemoveUser(user)} disabled={loading} className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={!!userToDelete} onClose={() => setUserToDelete(null)} title={t('organizations.fields.users.confirmRemoveTitle')} size="md">
        <div className="p-5">
          <p className="text-sm text-gray-800 dark:text-gray-200">{t('organizations.fields.users.confirmRemove')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{userToDelete?.email}</p>
          <div className="flex justify-end gap-2 mt-5">
            <button type="button" onClick={() => setUserToDelete(null)} disabled={loading} className="crm-btn crm-btn-ghost">{t('organizations.fields.users.cancelRemove')}</button>
            <button type="button" onClick={confirmRemoveUser} disabled={loading} className="crm-btn crm-btn-danger">{t('organizations.fields.users.remove')}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationUsers;
